import { useFieldArray, useFormContext, Controller, useWatch } from "react-hook-form";
import { TextField, IconButton, Stack, Button } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { styles } from "../estimateStyle.tsx";
import { EstimateDto, ROW_TYPE } from "./types.ts";
import React, { useState } from "react";
import { SortableRow } from "../../layouts/SortableRow.tsx"
import ContentHeaderFields from "./ContentHeaderFields.tsx";
import ItemRowFields from "./ItemRowFields.tsx";
import PdfPreviewSlot from "./PdfPreviewSlot.tsx";
import SortableList from "./SortableList.tsx";
import { useFieldArrayDnd } from "./useFieldArrayDnd.ts";

interface EstimateFormProps {
    onNext: () => void;
    onBack: () => void;
    onPreview: () => void;
    onDownload: () => void;
    onReset: () => void;
    contentPdfBlob: Blob | null;
    activeStep: number;
    totalSteps: number;
    loading: boolean;
}

export default function EstimateForm({
    onNext,
    onBack,
    onPreview,
    onDownload,
    onReset,
    contentPdfBlob,
    activeStep,
    totalSteps,
    loading
}: EstimateFormProps) {

    const { control, watch, setValue, getValues } = useFormContext<EstimateDto>();
    const { fields, append, remove, move } = useFieldArray<
        EstimateDto,
        "content.items"
    >({ control, name: "content.items" });

    useWatch({ name: "content.items" })
    // console.log(getValues())

    // ✅ 실제 현재 값은 useWatch로 받는 걸 권장
    const watchedItems = useWatch({ control, name: "content.items" }) ?? [];

    // --- row type 판별 (최소 수정 버전: remark 마커 우선) ---
    const isSubtotalRow = (row: any) =>
        row?.rowType === ROW_TYPE[2];

    const isDescRow = (row: any) =>
        row?.rowType === ROW_TYPE[1];

    const isItemRow = (row: any) =>
        row?.rowType === ROW_TYPE[0];

    // ✅ 소계 계산
    const calcSubtotalAt = React.useCallback(
        (subtotalIndex: number) => {
        const rows = getValues("content.items") ?? [];

        // 직전 소계 index 찾기
        let prevSubtotalIndex = -1;
        for (let i = subtotalIndex - 1; i >= 0; i--) {
            if (isSubtotalRow(rows[i])) {
            prevSubtotalIndex = i;
            break;
            }
        }

        const start = prevSubtotalIndex + 1; // 직전 소계 다음부터
        let sum = 0;

        for (let i = start; i < subtotalIndex; i++) {
            const r = rows[i];
            if (r?.amount > 0) {
            sum += Number(r?.amount ?? 0);
            }
        }

        setValue(`content.items.${subtotalIndex}.unitPrice`, sum, {
            shouldDirty: true,
            shouldValidate: true,
        });

        },
        [getValues, setValue]
    );

    // remark 배열용
    const {
        fields: remarkList,
        append: appendRemark,
        remove: removeRemark,
        move: moveRemark,
    } = useFieldArray<EstimateDto, "content.remark">({
        control,
        name: "content.remark",
    });

    // � 드래그 센서 + 종료 핸들러 (items / remarks)
    const { sensors, handleDragEnd } = useFieldArrayDnd(fields, move);
    const { handleDragEnd: handleDragEndRemark } = useFieldArrayDnd(remarkList, moveRemark);


    const addItem = () =>
        append({
            rowType: ROW_TYPE[0],
            item: "",
            size: "",
            unit: "",
            qty: 0,
            unitPrice: 0,
            amount: 0,
            remark: "",
            description: null
        })

    const addDesc = () =>
        append({
            rowType: ROW_TYPE[1],
            item: "",
            size: "",
            unit: "",
            qty: 0,
            unitPrice: 0,
            amount: 0,
            remark: "",
            description: ""
        })

    const addSubtotal= () =>
            append({
                rowType: ROW_TYPE[2],
                item: "",
                size: "",
                unit: "",
                qty: 0,
                unitPrice: 0,
                amount: 0,
                remark: "item:소계:",
                description: ""
            })


    const addRemark = () =>
        appendRemark("")

    return (
        <div className="p-4 space-y-4">
            <form style={styles.form}>
            <h2 style={styles.heading}>견적서 본문 작성</h2>
                <Stack spacing={4}>
                    <ContentHeaderFields />

                    {/* ------------- 드래그 가능한 리스트 ------------- */}
                    <SortableList
                        sensors={sensors}
                        onDragEnd={handleDragEnd}
                        ids={fields.map(f => f.id)}
                    >
                        {fields.map((item, index) => {
                            const row = watchedItems[index]; // ✅ 현재 값 기준으로 분기
                            const rowIsSubtotal = isSubtotalRow(row);
                            const rowIsDesc = isDescRow(row);

                            return (<SortableRow key={item.id} id={item.id}>
                                {
                                    rowIsSubtotal ? (
                                    // --- SUBTOTAL ROW ---
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <TextField
                                        label="구분"
                                        value="소계"
                                        sx={{ minWidth: 200 }}
                                        InputProps={{ readOnly: true }}
                                    />

                                    <IconButton onClick={() => remove(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                    </Stack>
                                ) : rowIsDesc ? (
                                    <Stack key={item.id} direction="row" spacing={2} alignItems="flex-start">
                                        {/* ── 설명 ── */}
                                        <Controller
                                            name={`content.items.${index}.description`}
                                            control={control}
                                            render={({ field }) => (
                                            <TextField {...field} label="설명" sx={{ minWidth: 1080 }} />
                                            )}
                                        />

                                        {/* 삭제 버튼 */}
                                        <IconButton onClick={() => remove(index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                        </Stack>
                                ) :
                                    (<ItemRowFields
                                        index={index}
                                        onRemove={() => remove(index)}
                                    />)
                                }
                            </SortableRow>
                            );
                        }
                        )}
                    </SortableList>

                    <Button onClick={ addItem }>
                        + 아이템 행 추가
                    </Button>
                    <Button onClick={ addDesc }>
                        + 설명 추가
                    </Button>
                    <Button onClick={ addSubtotal }>
                        + 소계 추가
                    </Button>

                    {/* 기타 사항 */}
                    <SortableList
                        sensors={sensors}
                        onDragEnd={handleDragEndRemark}
                        ids={remarkList.map(f => f.id)}
                    >
                        {remarkList.map((item, index) => (
                            <SortableRow key={item.id} id={item.id}>
                                <Stack key={item.id} direction="row" spacing={2} alignItems="flex-start">
                                    {/* ── 설명 ── */}
                                    <Controller
                                        name={`content.remark.${index}`}
                                        control={control}
                                        render={({ field }) => (
                                        <TextField {...field}
                                            label="기타사항"
                                            defaultValue={item[index]}
                                            sx={{ minWidth: 1080 }} />
                                        )}
                                    />

                                    {/* 삭제 버튼 */}
                                    <IconButton onClick={() => removeRemark(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            </SortableRow>
                        ))}
                    </SortableList>
                    <Button onClick={ addRemark }>
                        + 기타사항 행 추가
                    </Button>

                </Stack>

            </form>

            <div
            className="space-x-2"
            style={{
            display: "flex",
            justifyContent: "flex-end", // ① 오른쪽 끝으로 밀기
            gap: "0.5rem",              // ② 버튼 간 간격(기존 space-x-2 대체)
            }}
            >
                {activeStep > 0 && (
                    <Button onClick={onBack} color="inherit" style={styles.button}>
                        이전
                    </Button>
                )}
                <Button
                    onClick={onPreview}
                    style={styles.button}
                    >
                    미리보기 생성
                </Button>
                <Button variant="contained" onClick={onNext} style={styles.button}>
                    {activeStep === totalSteps - 1 ? "저장" : "다음"}
                </Button>
                <Button
                    onClick={onDownload}
                    disabled={!loading}
                    variant="contained"
                    color="primary"
                    // helperText={ "품명을 입력하세요" }
                    style={{
                        ...styles.button,
                        ...(!loading && styles.disable)
                        }}>
                    다운로드
                </Button>
                <Button
                    onClick={onReset}
                    style={styles.button}
                    >
                    초기화
                </Button>

            </div>
            <div
            className="space-x-2"
            style={{
            display: "flex",
            justifyContent: "flex-end", // ① 오른쪽 끝으로 밀기
            gap: "0.5rem",              // ② 버튼 간 간격(기존 space-x-2 대체)
            }}
            >
                <p style={styles.largeText}>
                    저장 후 다운로드하세요.
                </p>
            </div>

            <PdfPreviewSlot blob={contentPdfBlob} />
        </div>
    );
}
